import React, { useEffect, useState, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeContext } from "styled-components/macro";
import { ArrowRight } from "react-feather";
import { after, get, once } from "lodash";

import client from "../api/client/SdsWebApiClient";

import {
    SummaryItemsContainer,
    SummaryDisclaimer,
    ConfigurationImage,
    SummaryInfoItem,
    SummaryInfoItemMenu,
    SummaryInfoItemName,
    SummaryInfoItemPrice,
    SummaryInfoItemContainer,
    SummaryTotalPrice,
    FullscreenSummaryItems,
    RoomSurfaces,
} from "../components/Summary/SummaryItems";
import {
    CardsContainer,
    Card,
    CardTitle,
    CardDescription,
    CardButton,
    CardButtonText,
} from "../components/Summary/Cards";
import SendSummaryCard from "../components/Summary/SendSummaryCard";
import SendCustomSummaryCard from "../components/Summary/SendCustomSummaryCard";
import SendCustomSummaryCardToParent from "../components/Summary/SendCustomSummaryCardToParent";
import { ButtonBack } from "../components/NavigationButtons/ButtonBack";
import { AppLoader } from "../components/Loader/AppLoader";

import GaReporter, { GaEcommerceAction } from "../utilities/gaReporter";
import { useBreakpoint } from "../utilities/media-js-breakpoints";

import Constants from "../constants";

import {
    getBackendFlavor,
    getCurrentSummaries,
    getImageAndMask,
    getMetadata,
    getSessionIdSafe,
    getShowPrice,
    getShowProductInfo,
    getSummaryData,
    getTotalPriceText,
    getGuiResourceUrl,
    getModelId,
    getLanguage,
    getTotalPrice,
} from "../store/sagas/selectors";

import { CardData } from "../models/summary";

import FlipPhoneIcon from "../flipPhoneIcon.png";
import { SummaryGroup, SummaryItem } from "../models/item";
import { SdsBackendFlavor } from "../api/generated/models/SdsBackendFlavor";
import { TRIGGER_CALL_TO_ACTION } from "../store/actions/externalActions";
import { sanitizeHtml } from "../utilities/htmlSanitizer";

const SummaryContainer = styled.div`
    ${({ theme }) => theme.bp.xs`
        overflow-y: overlay;
        flex-flow: row wrap;
    `};
    ${({ theme }) => theme.bp.sm`
        flex-flow: row nowrap;
    `};
    background: ${({ theme }) => get(theme, "summary.backgroundColor")};
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-evenly;
    overflow: auto;
`;

const Summary = () => {
    const theme = useContext(ThemeContext);
    const dispatch = useDispatch();
    const summaryData = useSelector(getSummaryData);
    const sessionId = useSelector(getSessionIdSafe);
    const showPrice = useSelector(getShowPrice);
    const showProductInfo = useSelector(getShowProductInfo);
    const language = useSelector(getLanguage);

    const navigate = useNavigate();
    const { search } = useLocation();
    const breakpoints = useBreakpoint();

    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [blueprintConfig, setBlueprintConfig] = useState<string | null>(null);
    const [imageMissing, setImageMissing] = useState(false);
    const imageAndMask = useSelector(getImageAndMask);
    const summaryGroups = useSelector(getCurrentSummaries);
    const totalPrice = useSelector(getTotalPrice);
    const totalPriceText = useSelector(getTotalPriceText);
    const guiResourceUrl = useSelector(getGuiResourceUrl);
    const modelId = useSelector(getModelId);
    const metadata = useSelector(getMetadata);
    const backendFlavor = useSelector(getBackendFlavor);
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;
    const shouldDisplayFullscreenImage = isMobile && !breakpoints.portrait;
    const allItems = summaryGroups.flatMap(g => g.items);

    const searchParams = new URLSearchParams(window.location.search);
    const explicitSummaryCards = searchParams.get("summary_cards")?.split(",");

    const summariesGroupedByMenuText: SummaryGroup[] = [];
    const groups: { [key: string]: SummaryGroup } = {};
    summaryGroups.forEach((g: SummaryGroup) => {
        const textUpper = language ? g.menuText.toLocaleUpperCase(language) : g.menuText.toUpperCase();
        let group = groups[textUpper];
        if (group) {
            group.items = group.items.concat(g.items);
        } else {
            group = { menuId: g.menuId, menuText: g.menuText, items: g.items };
            groups[textUpper] = group;
            summariesGroupedByMenuText.push(group);
        }
    });

    useEffect(() => {
        GaReporter.trackPageView();
        GaReporter.updateTransactionId();
    }, []);

    useEffect(() => {
        setBlueprintConfig(null);
        if (!language || !sessionId) return;
        client
            .getConfigAsStringGet(sessionId, "mapping")
            .then((configurationString: string) => {
                if (!configurationString) return;
                setBlueprintConfig(configurationString);
            })
            .catch(() => {
                setBlueprintConfig(null);
            });
    }, [language, sessionId]);

    if (!summaryData) {
        return (
            <AppLoader
                color={get(theme, "appLoader.color", "#223861")}
                background={get(theme, "appLoader.backgroundColor")}
                height={200}
                width={80}
            />
        );
    }

    if (sessionId === undefined) {
        return (
            <AppLoader
                color={get(theme, "appLoader.color", "#223861")}
                background={get(theme, "appLoader.backgroundColor")}
                height={200}
                width={80}
            />
        );
    }

    const summaryCards: CardData[] = summaryData.cards.filter(
        cardData => cardData.enabled === undefined || cardData.enabled
    );

    const goBack = () => {
        navigate(`${Constants.configuratorUrlPath}${search}`);
    };

    const buildBlueprintLink = (baseLink: string) => {
        // Append property groups from the selected products as a query parameter to Blueprint.
        const propertyGroups: { [key: string]: { [key: string]: string } } = {};
        let anyProperties = false;
        const articleNumbers = new Set();
        allItems.forEach((item: SummaryItem) => {
            if (item.articleNumber) articleNumbers.add(item.articleNumber);
            if (metadata) {
                const itemMetadata = metadata[item.id];
                if (!itemMetadata) return;
                const pgs = itemMetadata[Constants.propertyGroupsMetadataKey];
                if (!pgs) return;
                pgs.toString()
                    .split(";")
                    .forEach((y: string) => {
                        const [assortmentId, rest]: string[] = y.split(":");
                        const [property, value] = rest.split("=");
                        if (!assortmentId || !property || !value) return;
                        propertyGroups[assortmentId] = propertyGroups[assortmentId] || {};
                        propertyGroups[assortmentId][property] = value;
                        anyProperties = true;
                    });
            }
        });
        const url = new URL(baseLink);

        // Property groups sets the default options like color or style
        // for furniture in Blueprint.
        if (anyProperties) url.searchParams.set("sboproperties", JSON.stringify(propertyGroups));

        // Adding article numbers to add the selected tiles to the palette in Blueprint.
        if (articleNumbers.size > 0) url.searchParams.set("sboarticles", Array.from(articleNumbers).join(","));

        // Add configuration string to add selected items by retailer id to palette in Blueprint.
        if (backendFlavor === SdsBackendFlavor.SdsSwift && blueprintConfig)
            url.searchParams.set("sdsconfig", blueprintConfig);

        // Add retailer id to know in Blueprint which retailer the SDS link was from.
        const retailerId = searchParams.get("retailer_id");
        if (retailerId) url.searchParams.set("sboretailer", retailerId);

        return url.toString();
    };

    const handleCardButtonClick = async (cardData: CardData) => {
        const openLink = (link: string) => {
            if (cardData.openInSameTab && window.top) {
                window.top.location.href = link;
            } else {
                // Note: Would be a little more secure to pass "noopener,noreferrer" using the third argument,
                // but Safari opens the link in a new window instead of tab in that case.
                // https://discussions.apple.com/thread/253020466
                const win = window.open(link, "_blank");
                if (win) win.opener = null;
            }
        };

        const openLinkAsync = async (linkPromise: Promise<string>) => {
            if (cardData.openInSameTab && window.top) {
                const link = await linkPromise;
                if (link) window.top.location.href = link;
            } else {
                const win = window.open("about:blank", "_blank");
                if (!win) return;
                win.opener = null;
                const link = await linkPromise;
                if (link) {
                    win.location.href = link;
                } else {
                    win.close();
                }
            }
        };

        const handleActionClick = async (action: string) => {
            if (action === "SEND_TO_WEBSHOP") {
                const retailerId = searchParams.get("retailer_id");
                const linkPromise = client
                    .getOrderSpecificationFromSummaryPost(sessionId, retailerId || undefined)
                    .then((xmlUrl: string) => cardData.webshopUrl + xmlUrl);
                openLinkAsync(linkPromise);
            } else if (action === "SEND_TO_WEBSHOP_V2") {
                const retailerId = searchParams.get("retailer_id");
                const linkPromise = client
                    .getOrderSpecificationV2Post(sessionId, retailerId || undefined)
                    .then((xmlUrl: string) => cardData.webshopUrl + xmlUrl);
                openLinkAsync(linkPromise);
            } else {
                dispatch({
                    type: TRIGGER_CALL_TO_ACTION,
                    action,
                });
            }
        };

        if (cardData.buttonAction) {
            const { buttonAction } = cardData;
            const delayedCallback = after(
                2,
                once(() => handleActionClick(buttonAction))
            );
            if (buttonAction === "SEND_TO_WEBSHOP" || buttonAction === "SEND_TO_WEBSHOP_V2") {
                GaReporter.event("cta_send_to_webshop", "CTA", undefined, undefined, delayedCallback);
            } else {
                GaReporter.event("cta_custom_action", "CTA", buttonAction, undefined, delayedCallback);
            }
            GaReporter.trackProductCheckout(
                GaEcommerceAction.Purchase,
                buttonAction,
                allItems,
                totalPrice,
                delayedCallback
            );
        }

        const buttonLink = cardData.buttonLink;
        if (buttonLink) {
            if (cardData.isBlueprintLink) {
                if (!blueprintConfig) {
                    // Wait for backend to provide the mapped config.
                    return;
                }

                const bpLink = buildBlueprintLink(buttonLink);
                const delayedCallback = after(
                    2,
                    once(() => openLink(bpLink))
                );

                GaReporter.event("cta_blueprint_link", "CTA", undefined, undefined, delayedCallback);
                GaReporter.trackProductCheckout(
                    GaEcommerceAction.Purchase,
                    "BLUEPRINT_LINK",
                    allItems,
                    totalPrice,
                    delayedCallback
                );
            } else {
                GaReporter.event("cta_link_click", "CTA", buttonLink, undefined, () => openLink(buttonLink));
            }
        }
    };

    const renderConfigurationImage = (fullscreen: boolean) => {
        return (
            <ConfigurationImage
                ref={imageRef}
                alt="alt"
                fullscreen={fullscreen}
                src={imageAndMask?.configurationImagePath || ""}
            />
        );
    };

    const renderCard = (cardData: CardData) => {
        if (explicitSummaryCards && !explicitSummaryCards.includes(cardData.id)) {
            return null;
        }

        if (cardData.buttonAction === "SUMMARY_EMAIL") {
            return <SendSummaryCard key={cardData.title} config={cardData} />;
        }

        if (cardData.buttonAction === "SEND_TO_SHOP") {
            return <SendCustomSummaryCard key={cardData.title} config={cardData} />;
        }

        if (cardData.buttonAction === "SEND_TO_PARENT") {
            return <SendCustomSummaryCardToParent key={cardData.title} config={cardData} />;
        }

        return (
            <Card key={cardData.title}>
                <CardTitle>{cardData.title}</CardTitle>
                {cardData.description ? (
                    <CardDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(cardData.description) }} />
                ) : null}
                <CardButton
                    onClick={() => {
                        handleCardButtonClick(cardData);
                    }}
                >
                    <CardButtonText>{cardData.buttonText}</CardButtonText>
                    <ArrowRight size={breakpoints.xxs || breakpoints.xs ? 16 : 22} color="#fff" />
                </CardButton>
            </Card>
        );
    };

    const renderRoomSurfaceInformationImage = () => {
        let url = "";
        if (guiResourceUrl && modelId) {
            url = `${guiResourceUrl}img/${modelId}_surfaces.png`;
        }

        return (
            <>
                {!imageMissing ? (
                    <RoomSurfaces>
                        <img alt="alt" onError={() => setImageMissing(true)} src={url} />
                    </RoomSurfaces>
                ) : null}
            </>
        );
    };

    if (shouldDisplayFullscreenImage) {
        return (
            <>
                {renderConfigurationImage(true)}
                <img
                    src={FlipPhoneIcon}
                    style={{
                        position: "absolute",
                        width: 18,
                        height: 18,
                        top: 0,
                        right: 0,
                        margin: 30,
                    }}
                    alt="Flip phone icon"
                />
            </>
        );
    }

    const renderSummary = (fullscreen: boolean) => {
        return (
            <>
                <ConfigurationImage
                    ref={imageRef}
                    style={{ display: "flex" }}
                    alt="alt"
                    fullscreen={fullscreen}
                    src={imageAndMask?.configurationImagePath || ""}
                />
                {showProductInfo ? renderRoomSurfaceInformationImage() : null}
                {showProductInfo ? (
                    <FullscreenSummaryItems>
                        {summariesGroupedByMenuText.map(group => {
                            return (
                                <SummaryInfoItem fullscreen key={group.menuId}>
                                    <SummaryInfoItemMenu>{group.menuText}</SummaryInfoItemMenu>
                                    {group.items.map((item: SummaryItem) => (
                                        <SummaryInfoItemContainer key={item.id}>
                                            <SummaryInfoItemName>{item.name}</SummaryInfoItemName>
                                            {showPrice ? (
                                                <SummaryInfoItemPrice>{item.priceText}</SummaryInfoItemPrice>
                                            ) : null}
                                        </SummaryInfoItemContainer>
                                    ))}
                                </SummaryInfoItem>
                            );
                        })}
                    </FullscreenSummaryItems>
                ) : null}
                {summaryData.totalPrice && showPrice ? <SummaryTotalPrice>{totalPriceText}</SummaryTotalPrice> : null}
                {summaryData.disclaimerText ? (
                    <SummaryDisclaimer dangerouslySetInnerHTML={{ __html: sanitizeHtml(summaryData.disclaimerText) }} />
                ) : null}
            </>
        );
    };

    return (
        <SummaryContainer>
            <SummaryItemsContainer ref={containerRef}>{renderSummary(false)}</SummaryItemsContainer>
            <CardsContainer>{summaryCards.map(cardData => renderCard(cardData))}</CardsContainer>
            <ButtonBack sticky onClick={goBack} />
        </SummaryContainer>
    );
};

export default Summary;
